import React, { useCallback, useEffect, useRef, useState } from 'react';
import { carteraApi } from './api';
import { money, iniciales, avaColor, EtapaChip, vehiculoTxt } from './helpers';
import { RegistrarPagoModal } from './RegistrarPagoModal';
import { RevertirPagoModal } from './RevertirPagoModal';
import { RecuperarAutoModal } from './RecuperarAutoModal';
import { RevertirRecuperacionModal } from './RevertirRecuperacionModal';
import { CambiarResultadoBuroModal, FORMA_TXT, hoyLocal } from './CambiarResultadoBuroModal';

const ESTATUS_LABEL = {
  pagado: 'Pagado', paid: 'Pagado', pendiente: 'Pendiente', pending: 'Pendiente',
  scheduled: 'Programado', pago_parcial: 'Parcial', partial: 'Parcial',
  vencido: 'Vencido', overdue: 'Vencido',
  waived: 'Condonada', condonado: 'Condonada',
};

// Etiqueta de una parcialidad: las condonadas por recuperación de auto lo dicen.
const estatusLabel = (p) => (
  ['waived', 'condonado'].includes(p.estatus) && p.condonada_recuperacion
    ? 'Condonada (recuperación)'
    : (ESTATUS_LABEL[p.estatus] || p.estatus)
);

// Estatus del RECIBO (tabla pagos), distinto al de la parcialidad. 'cancelado'
// se muestra como "Revertido": el pago se deshizo pero el recibo sigue vivo y
// visible con su folio quemado (decisión del usuario: NO tachado).
const PAGO_ESTATUS = {
  aplicado: 'Aplicado', pendiente_revision: 'En revisión',
  cancelado: 'Revertido', rechazado: 'Rechazado',
};
// Clase del chip por estatus del recibo (las de pago-badge del módulo).
const PAGO_BADGE = {
  aplicado: 'pagado', pendiente_revision: 'pendiente',
  cancelado: 'revertido', rechazado: 'rechazado',
};

const fechaCorta = (s) => String(s || '').slice(0, 10);

// Saldos que se siguen cobrando tras recuperar el auto. El aval solo cuenta si
// cobra por su cuenta: con «igual» sigue al titular y no tiene estado propio.
const cobrosRecuperacion = (cierre) => {
  const rb = cierre?.resultado_buro;
  if (!rb) return [];
  const monto = (b) => b.monto_pendiente ?? cierre.faltante;
  const out = [];
  if (rb.titular?.resultado === 'cobra') {
    out.push({ persona: 'titular', quien: 'Titular', monto: monto(rb.titular), pagado: !!rb.titular.pagado, fecha_pago: rb.titular.fecha_pago });
  }
  if (cierre.tiene_aval && cierre.aval_con_resultado !== false && rb.aval?.resultado === 'cobra') {
    out.push({ persona: 'aval', quien: cierre.aval_nombre ? `Aval ${cierre.aval_nombre}` : 'Aval', monto: monto(rb.aval), pagado: !!rb.aval.pagado, fecha_pago: rb.aval.fecha_pago });
  }
  return out;
};

const textoResultadoBuro = (b, cierre) => {
  switch (b?.resultado) {
    case 'cubre': return 'El auto cubre la deuda';
    case 'perdona': return `Se perdona lo que falta (${money(cierre.faltante)})`;
    case 'cobra': return `Se sigue cobrando · saldo pendiente ${money(b.monto_pendiente ?? cierre.faltante)}`;
    case 'igual': return 'Igual que el titular';
    default: return '—';
  }
};

/**
 * Parte del banner de recuperación con el resultado para Buró del titular y del
 * aval. Si alguno sigue cobrándose, deja marcar «pagado» (con fecha) o «no
 * pagado». onCierre(cierreNuevo) lo entrega al padre para pintar el cambio.
 */
const ResultadoBuroBanner = ({ creditoId, cierre, onCierre }) => {
  const [pagoForm, setPagoForm] = useState(null); // { persona, fecha }
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const rb = cierre.resultado_buro;
  const entrega = fechaCorta(cierre.fecha_entrega);

  async function enviar(persona, pagado, fecha) {
    setBusy(true);
    setErr(null);
    try {
      const res = await carteraApi.recuperacionBuroPago(creditoId, { persona, pagado, ...(pagado ? { fecha_pago: fecha } : {}) });
      setPagoForm(null);
      onCierre(res?.data?.data?.cierre ?? null);
    } catch (e) {
      setErr(e?.response?.data?.message || 'No se pudo guardar el pago.');
    } finally {
      setBusy(false);
    }
  }

  function confirmarPago() {
    const f = pagoForm?.fecha;
    if (!f) { setErr('Indica la fecha de pago.'); return; }
    if (f > hoyLocal()) { setErr('La fecha de pago no puede ser futura.'); return; }
    if (entrega && f < entrega) { setErr('La fecha de pago no puede ser anterior a la entrega del auto.'); return; }
    enviar(pagoForm.persona, true, f);
  }

  // Función (no componente): se llama dos veces, titular y aval.
  const linea = (persona, titulo, b) => {
    const cobra = b?.resultado === 'cobra';
    const abierto = pagoForm?.persona === persona;
    return (
      <div key={persona} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span>
          <b>{titulo}:</b> {textoResultadoBuro(b, cierre)}
          {cobra && <> · {b.pagado ? `Pagado el ${fechaCorta(b.fecha_pago)}` : 'No pagado'}</>}
        </span>
        {cobra && !abierto && (
          <>
            {!b.pagado && (
              <button type="button" className="btn btn-sm" disabled={busy}
                onClick={() => { setErr(null); setPagoForm({ persona, fecha: hoyLocal() }); }}>
                <i className="pi pi-check" />Marcar pagado
              </button>
            )}
            {b.pagado && (
              <>
                <button type="button" className="btn btn-sm" disabled={busy}
                  onClick={() => { setErr(null); setPagoForm({ persona, fecha: fechaCorta(b.fecha_pago) || hoyLocal() }); }}>
                  <i className="pi pi-calendar" />Cambiar fecha
                </button>
                <button type="button" className="btn btn-sm" disabled={busy} onClick={() => enviar(persona, false)}>
                  <i className="pi pi-times" />No pagado
                </button>
              </>
            )}
          </>
        )}
        {cobra && abierto && (
          <>
            <input type="date" value={pagoForm.fecha} min={entrega || undefined} max={hoyLocal()} disabled={busy}
              aria-label={`Fecha de pago · ${titulo}`}
              onChange={(e) => setPagoForm({ persona, fecha: e.target.value })}
              style={{ padding: '3px 8px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13 }} />
            <button type="button" className="btn btn-green btn-sm" disabled={busy} onClick={confirmarPago}>
              <i className="pi pi-check" />{busy ? 'Guardando…' : 'Guardar'}
            </button>
            <button type="button" className="btn btn-sm" disabled={busy} onClick={() => { setPagoForm(null); setErr(null); }}>Cancelar</button>
          </>
        )}
      </div>
    );
  };

  return (
    <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid rgba(91, 33, 182, .25)', fontWeight: 500, display: 'grid', gap: 6 }}>
      <div style={{ fontWeight: 700 }}>Resultado para Buró</div>
      {!rb ? (
        <div>Sin resultado guardado para Buró</div>
      ) : (
        <>
          {linea('titular', 'Titular', rb.titular)}
          {cierre.tiene_aval && cierre.aval_con_resultado !== false && linea('aval', cierre.aval_nombre ? `Aval ${cierre.aval_nombre}` : 'Aval', rb.aval)}
        </>
      )}
      {err && <div style={{ color: '#b91c1c', fontWeight: 600 }}>{err}</div>}
    </div>
  );
};

export const CarteraDetalle = ({ id, onBack }) => {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [seccion, setSeccion] = useState('cuenta'); // cuenta | pagos
  const [showPago, setShowPago] = useState(false);
  const [showRecuperar, setShowRecuperar] = useState(false);
  const [showDeshacerRec, setShowDeshacerRec] = useState(false);
  const [showCambiarBuro, setShowCambiarBuro] = useState(false);
  // Acción sobre un pago ya registrado: {pago, modo, accion}. modo =
  // revertir | corregir | condonar; accion (solo condonar) = aplicar | quitar.
  const [accionPago, setAccionPago] = useState(null);
  const [revisando, setRevisando] = useState(false);
  // Edición de tasa (AP): editor inline en la card de Tasa anual.
  const [editandoTasa, setEditandoTasa] = useState(false);
  const [tasaInput, setTasaInput] = useState('');
  const [guardandoTasa, setGuardandoTasa] = useState(false);
  const [tasaErr, setTasaErr] = useState(null);
  const abortRef = useRef(null);

  const load = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    setError(null);
    try {
      const res = await carteraApi.getCredito(id, ctrl.signal);
      if (ctrl.signal.aborted) return;
      setD(res?.data?.data || res?.data || null);
    } catch (e) {
      if (ctrl.signal.aborted || e?.code === 'ERR_CANCELED') return;
      setError(e?.response?.data?.message || 'No se pudo cargar el crédito.');
    } finally {
      if (!ctrl.signal.aborted) setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
    return () => { if (abortRef.current) abortRef.current.abort(); };
  }, [load]);

  async function handleRevisar() {
    setRevisando(true);
    try {
      await carteraApi.marcarRevisado(id);
      await load();
    } catch (e) {
      setError(e?.response?.data?.message || 'No se pudo marcar como revisado.');
    } finally {
      setRevisando(false);
    }
  }

  async function guardarTasa() {
    const val = parseFloat(String(tasaInput).replace(/[^0-9.]/g, ''));
    if (!Number.isFinite(val) || val <= 0) { setTasaErr('Escribe una tasa mayor a 0.'); return; }
    setGuardandoTasa(true);
    setTasaErr(null);
    try {
      const res = await carteraApi.editarTasa(id, val);
      const pct = res?.data?.data?.tasa_pct;
      // Update silencioso (sin recargar → sin remount): solo la tasa del resumen.
      setD((prev) => (prev ? { ...prev, resumen: { ...prev.resumen, tasa: pct } } : prev));
      setEditandoTasa(false);
    } catch (e) {
      setTasaErr(e?.response?.data?.message || 'No se pudo actualizar la tasa.');
    } finally {
      setGuardandoTasa(false);
    }
  }

  // Tras marcar un pago de la recuperación el back devuelve el cierre completo:
  // se pinta sin recargar la página (nada más del crédito cambió).
  function aplicarCierre(nuevo) {
    if (!nuevo) { load(); return; }
    setD((prev) => (prev ? { ...prev, resumen: { ...prev.resumen, cierre: nuevo } } : prev));
  }

  if (loading) return <div className="cart-loading">Cargando crédito…</div>;
  if (error) return <div className="cart-loading">{error}</div>;
  if (!d) return null;

  const r = d.resumen || {};
  const totales = d.totales || {};
  const cuenta = d.estado_de_cuenta || [];
  const pagos = d.pagos || [];
  const esRenta = cuenta[0]?.tipo === 'renta';
  // Cierre por recuperación de auto o por liquidación anticipada (null si no).
  const cierre = (r.cierre ?? d.cierre ?? null);
  const recuperado = cierre?.motivo === 'recuperacion_auto';
  const liquidadoAnticipado = cierre?.motivo === 'liquidacion_anticipada';
  const credVivo = !['liquidado', 'castigado'].includes(r.estatus) && !recuperado;
  const difRec = recuperado && cierre.valor_auto != null && cierre.diferencia != null ? Number(cierre.diferencia) : null;
  // Saldos que se siguen cobrando tras la recuperación (para la fila final de la tabla).
  const cobrosRec = recuperado ? cobrosRecuperacion(cierre) : [];
  // AP: la tasa es solo display/analítica → editable inline. AF/CS requiere
  // regenerar amortización (otro flujo), por eso el lápiz solo sale en AP.
  const esAP = (r.tipo_label || r.tipo) === 'AP' || r.tipo === 'arrendamiento_puro';
  // Puro: el "saldo por cobrar" son las rentas pendientes (con IVA); el total del
  // contrato es la suma de todas las rentas. El monto financiado es solo referencia.
  const totalContrato = esRenta
    ? cuenta.reduce((s, p) => s + Number(p.pago_total || 0), 0)
    : null;

  // Servicios mensuales (seguro/placas/GPS): monto plano del crédito. En AF/CS el
  // pago del mes = cuota + servicios; el cobro los reparte (mora→servicios→cuota).
  const serviciosMes = !esRenta ? Number(r.servicios_mensual || 0) : 0;
  // Desglose de accesorios del cotizador (seguro/placas/GPS/IVA). Los ceros se muestran.
  const desglose = r.servicios_desglose || null;
  // Puro: desglose INFORMATIVO de la renta (ya incluida; no se cobra aparte). Suma = renta.
  const rentaDesglose = esRenta ? (r.renta_desglose || null) : null;
  // Columnas de desglose para la tabla: Financiera siempre; servicios solo si > 0
  // (los $0 quedan explícitos en la tarjeta de arriba como "· sin").
  const desgCols = rentaDesglose ? [
    ['Financiera', Number(rentaDesglose.financiera || 0)],
    ['Seguro', Number(rentaDesglose.seguro || 0)],
    ['Placas', Number(rentaDesglose.placas || 0)],
    ['GPS', Number(rentaDesglose.gps || 0)],
    // Alta manual: un solo monto "Servicios" (sin desglosar por concepto).
    ['Servicios', Number(rentaDesglose.servicios || 0)],
    ['Otros', Number(rentaDesglose.otros || 0)],
  ].filter(([, v], i) => i === 0 || v > 0) : [];
  const desgSuma = desgCols.reduce((s, [, v]) => s + v, 0); // = renta estándar (suma validada al guardar)
  // Amortización REFERENCIAL del puro (composición interés/capital de cada renta,
  // cierra en el residual — igual que financiero). El backend la manda por fila
  // ('amort') solo cuando monto/tasa/residual del crédito ya cuadran con el cotizador.
  const amortPuro = esRenta && cuenta.some((p) => p.amort);
  const amortSplit = amortPuro && cuenta.some((p) => p.amort && p.amort.seguro != null);
  // Residual del contrato (arrendamiento financiero): el saldo baja hasta aquí, no a 0.
  const residual = Number(r.residual || 0);
  // Abonos a capital aplicados (cada uno regenera la tabla pendiente con la misma
  // cuota). El back los expone en el resumen (abonos_capital) o dentro de meta
  // ({fecha, monto, ahorro}); tolerante a ambas rutas.
  const abonosRaw = r.abonos_capital ?? r.meta?.abonos_capital ?? d.meta?.abonos_capital;
  const abonosCapital = !esRenta && Array.isArray(abonosRaw) ? abonosRaw : [];
  // Lo que un ítem debe en total: cuota restante + servicios del mes restantes.
  const debeDe = (p) =>
    (Number(p.pago_total || 0) - Number(p.monto_pagado || 0))
    + (serviciosMes > 0 ? Math.max(0, serviciosMes - Number(p.servicios_pagado || 0)) : 0);

  // Filas NO pagadas (pendiente/parcial/vencido): base de "Σ Por cobrar", de las
  // notas de identidad y de las sugerencias del modal. AF/CS marca 'pagado';
  // AP marca 'paid' ('cancelled'/'waived' tampoco cuentan).
  const itemsPend = cuenta.filter((p) => !['paid', 'pagado', 'cancelled', 'waived', 'condonado'].includes(p.estatus));

  // Σ del estado de cuenta: sumas defensivas sobre las filas renderizadas.
  // "Σ Total" = todas las filas (contrato completo); "Σ Por cobrar" = solo lo que
  // FALTA: en filas parciales se descuenta lo ya abonado replicando la jerarquía
  // del motor (IVA → interés → capital dentro de la cuota; servicios aparte vía
  // servicios_pagado). Así la columna Capital empata a simple vista con el saldo
  // insoluto (el check del jefe). Los saldos no se suman (no significan nada).
  const sum = (fn) => cuenta.reduce((s, p) => s + (Number(fn(p)) || 0), 0);
  const sumPend = (fn) => itemsPend.reduce((s, p) => s + (Number(fn(p)) || 0), 0);
  // Restantes de una fila AF/CS (waterfall IVA → interés → capital, como
  // RegistrarPagoService al aplicar un pago a la cuota).
  const restanteDe = (p) => {
    const pagado = Number(p.monto_pagado) || 0;
    const iva = Number(p.iva_interes) || 0;
    const int = Number(p.interes) || 0;
    const cap = Number(p.capital) || 0;
    const aIva = Math.min(iva, pagado);
    const aInt = Math.min(int, Math.max(0, pagado - iva));
    const aCap = Math.min(cap, Math.max(0, pagado - iva - int));
    return {
      iva: iva - aIva,
      interes: int - aInt,
      capital: cap - aCap,
      cuota: Math.max(0, (Number(p.pago_total) || 0) - pagado),
      servicios: serviciosMes > 0 ? Math.max(0, serviciosMes - (Number(p.servicios_pagado) || 0)) : 0,
    };
  };
  const sumPendRest = (key) => itemsPend.reduce((s, p) => s + restanteDe(p)[key], 0);
  // Renta/cuota por cobrar (genérico, AP incluido): pago_total − abonado.
  const sumPendResta = () => itemsPend.reduce(
    (s, p) => s + Math.max(0, (Number(p.pago_total) || 0) - (Number(p.monto_pagado) || 0)), 0);
  // Filas del puro simple que sí muestran el desglose (renta del mes == desgSuma);
  // los totales de cada columna de desglose replican ese mismo condicional.
  const desgFilas = cuenta.filter((p) => Math.abs(Number(p.pago_total || 0) - desgSuma) <= 1).length;
  const desgFilasPend = itemsPend.filter((p) => Math.abs(Number(p.pago_total || 0) - desgSuma) <= 1).length;
  // Número de columnas de la tabla según su variante (mismo condicional del thead).
  const numCols = esRenta
    ? (amortPuro ? 10 + (amortSplit ? 4 : 1) : 6 + desgCols.length)
    : 8 + (serviciosMes > 0 ? 2 : 0);

  // ── Identidad contable (nota bajo la tabla; cuadre a ±$1) ────────────────
  // AF/CS — Check A: Σ capital pendiente AJUSTADO (+ residual) ≈ saldo insoluto.
  // El ajuste replica RegistrarPagoService::recalcularSaldo para pago_parcial
  // (el abono se aplica IVA → interés → capital): el capital vivo de la fila es
  // max(0, capital − max(0, pagado − interés − IVA)).
  // AF/CS — Check B (informativo): Σ capital de TODA la tabla + abonos a capital
  // (+ residual) ≈ monto financiado.
  // AP — Σ(renta − pagado) de las no pagadas ≈ saldo por cobrar.
  // Si falta el dato base (saldo/financiado) el check no se muestra.
  const saldoInsoluto = totales.saldo_insoluto ?? r.saldo_insoluto;
  const cuadra = (a, b) => Math.abs((Number(a) || 0) - (Number(b) || 0)) <= 1;
  const notasIdentidad = [];
  if (cuenta.length > 0 && !esRenta && saldoInsoluto != null) {
    const capPendiente = itemsPend.reduce((s, p) => {
      const aCapital = Math.max(0, (Number(p.monto_pagado) || 0) - (Number(p.interes) || 0) - (Number(p.iva_interes) || 0));
      return s + Math.max(0, (Number(p.capital) || 0) - aCapital);
    }, 0);
    const okA = cuadra(capPendiente + residual, saldoInsoluto);
    notasIdentidad.push({
      ok: okA,
      texto: `Σ capital pendiente ${money(capPendiente)}`
        + (residual > 0 ? ` + residual ${money(residual)}` : '')
        + ` ${okA ? '=' : '≠'} saldo insoluto ${money(saldoInsoluto)}`,
    });
    if (r.monto_financiado != null) {
      const capTotal = sum((p) => p.capital);
      const abonosMonto = abonosCapital.reduce((s, a) => s + (Number(a.monto) || 0), 0);
      const okB = cuadra(capTotal + abonosMonto + residual, r.monto_financiado);
      notasIdentidad.push({
        ok: okB,
        texto: `capital de la tabla ${money(capTotal)}`
          + (abonosMonto > 0 ? ` + abonos a capital ${money(abonosMonto)}` : '')
          + (residual > 0 ? ` + residual ${money(residual)}` : '')
          + ` ${okB ? '=' : '≠'} financiado ${money(r.monto_financiado)}`,
      });
    }
  }
  if (cuenta.length > 0 && esRenta && saldoInsoluto != null) {
    const rentasPorCobrar = itemsPend.reduce(
      (s, p) => s + ((Number(p.pago_total) || 0) - (Number(p.monto_pagado) || 0)), 0);
    const okR = cuadra(rentasPorCobrar, saldoInsoluto);
    notasIdentidad.push({
      ok: okR,
      texto: `rentas por cobrar ${money(rentasPorCobrar)} ${okR ? '=' : '≠'} saldo por cobrar ${money(saldoInsoluto)}`,
    });
  }

  // Sugerencias de monto para el modal: la mensualidad (próximo ítem sin pagar) y,
  // si hay vencidos, cuánto se necesita para ponerse al corriente (restante vencido + mora).
  const hoyISO = new Date().toISOString().slice(0, 10);
  const cuotaBase = itemsPend[0] ? Number(itemsPend[0].pago_total || 0) : 0;
  const proximaMensualidad = itemsPend[0] ? cuotaBase + serviciosMes : 0; // mes completo (cuota + servicios)
  const moraActual = Number((totales.mora_acumulada ?? r.mora_acumulada) || 0);
  const vencidoRestante = itemsPend
    .filter((p) => p.fecha_vencimiento && p.fecha_vencimiento < hoyISO)
    .reduce((s, p) => s + debeDe(p), 0);
  const alCorriente = vencidoRestante + moraActual;
  // Tope: todo lo que resta del contrato (cuotas + servicios) + mora.
  const totalPendiente = itemsPend.reduce((s, p) => s + debeDe(p), 0);
  const maxPagar = totalPendiente + moraActual;
  // Si el mes en curso viene abonado parcialmente (cuota o servicios), cuánto falta
  // para completarlo (deja saldar ese pedacito sin recorrer el faltante al siguiente).
  const proximoItem = itemsPend[0];
  const completarCuota = proximoItem && (Number(proximoItem.monto_pagado || 0) > 0 || Number(proximoItem.servicios_pagado || 0) > 0)
    ? debeDe(proximoItem)
    : 0;

  return (
    <>
      <button className="cart-back" onClick={onBack}><i className="pi pi-arrow-left" />Volver a la cartera</button>

      {/* Header */}
      <div className="detail-head">
        <div className="ava-lg" style={{ background: avaColor(r.cliente || r.clave_contrato) }}>{iniciales(r.cliente)}</div>
        <div style={{ flex: 1 }}>
          <div className="dh-nm">{r.cliente || '—'}</div>
          <div className="dh-sub">{r.clave_contrato} · {r.tipo_label || r.tipo} · {vehiculoTxt(r.vehiculo)}</div>
        </div>
        <EtapaChip etapa={r.etapa_cobranza} estatus={r.estatus} enRevision={r.en_revision} recuperado={recuperado} sub={r.dias_vencido ? r.dias_vencido + 'd' : null} />
        {!recuperado && !liquidadoAnticipado && (
          <button className="btn btn-green btn-sm" style={{ marginLeft: 12 }} onClick={() => setShowPago(true)}><i className="pi pi-plus" />Registrar pago</button>
        )}
        {credVivo && (
          <button className="btn btn-sm" style={{ marginLeft: 8 }} onClick={() => setShowRecuperar(true)}><i className="pi pi-car" />Liquidar por recuperación de auto</button>
        )}
      </div>

      {recuperado && (
        <div style={{ background: '#EDE9FE', color: '#5B21B6', borderRadius: 12, padding: '10px 14px', fontSize: 13, fontWeight: 600, marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <i className="pi pi-car" />
            <span style={{ flex: 1 }}>
              Liquidado por recuperación de auto · {fechaCorta(cierre.fecha_entrega)} · {FORMA_TXT[cierre.forma] || FORMA_TXT.entrega} · Capital cubierto {money(cierre.capital_cubierto)}
              {cierre.valor_auto != null && <> · Valor del auto {money(cierre.valor_auto)}</>}
              {difRec != null && (difRec < 0 ? <> · Pérdida {money(Math.abs(difRec))}</> : <> · Ganancia {money(difRec)}</>)}
              {cierre.por_nombre && <span style={{ fontWeight: 500 }}> · Registrado por {cierre.por_nombre}</span>}
            </span>
            <button className="btn btn-sm" onClick={() => setShowCambiarBuro(true)}><i className="pi pi-pencil" />Cambiar resultado</button>
            <button className="btn btn-sm" onClick={() => setShowDeshacerRec(true)}><i className="pi pi-undo" />Deshacer recuperación</button>
          </div>
          <ResultadoBuroBanner creditoId={id} cierre={cierre} onCierre={aplicarCierre} />
        </div>
      )}

      {liquidadoAnticipado && (
        <div style={{ background: '#E7F6EE', color: '#06794b', borderRadius: 12, padding: '10px 14px', fontSize: 13, fontWeight: 600, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <i className="pi pi-check-circle" />
          <span style={{ flex: 1 }}>
            Liquidado por anticipado · {fechaCorta(cierre.fecha)}
            {cierre.folio && <> · Folio {cierre.folio}</>}
            {cierre.total != null && <> · Pagó {money(cierre.total)}</>}
            {Number(cierre.mora_condonada) > 0 && <> · Mora condonada {money(cierre.mora_condonada)}</>}
            <span style={{ fontWeight: 500 }}> · Ya no admite pagos.</span>
          </span>
        </div>
      )}

      {r.en_revision && (
        <div style={{ background: '#FEF3C7', color: '#92400E', borderRadius: 12, padding: '10px 14px', fontSize: 13, fontWeight: 600, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
          <i className="pi pi-exclamation-triangle" />
          <span style={{ flex: 1 }}>En revisión: {r.revision_motivo || 'verificar datos importados.'}</span>
          <button className="btn btn-green btn-sm" onClick={handleRevisar} disabled={revisando}>
            <i className="pi pi-check" />{revisando ? 'Aplicando…' : 'Marcar como revisado'}
          </button>
        </div>
      )}

      {/* Resumen */}
      <div className="stat-grid">
        <div className="stat-card">
          <div className="sc-label">{esRenta ? 'Saldo por cobrar' : 'Saldo insoluto'}</div>
          <div className="sc-val">{money(totales.saldo_insoluto ?? r.saldo_insoluto)}</div>
          <div className="sc-sub">
            {esRenta
              ? <>de {money(totalContrato)} total · financiado {money(r.monto_financiado)}</>
              : <>de {money(r.monto_financiado)} financiado</>}
          </div>
          {/* Valor residual: en PURO es la opción de compra al final (no baja el saldo de
              rentas); en FINANCIERO con residual (AF004/AF006) el saldo YA lo incluye. */}
          {residual > 0 && (
            <div className="sc-sub" style={{ marginTop: 2 }}>
              <i className="pi pi-flag" style={{ fontSize: 11, color: '#2BA7CE', marginRight: 4 }} />
              Valor residual <b>{money(residual)}</b>{esRenta ? ' (compra al final)' : ''}
            </div>
          )}
        </div>
        <div className="stat-card">
          <div className="sc-label">Avance</div>
          <div className="sc-val">{r.cuotas?.pagadas || 0}/{r.cuotas?.total || 0}</div>
          <div className="sc-sub">{r.cuotas?.total ? Math.round((r.cuotas.pagadas / r.cuotas.total) * 100) : 0}% pagado</div>
        </div>
        <div className="stat-card">
          <div className="sc-label">
            Tasa anual
            {esAP && !editandoTasa && (
              <button type="button" className="sc-edit"
                title="Editar tasa"
                onClick={() => { setTasaInput(r.tasa != null ? String(r.tasa) : ''); setTasaErr(null); setEditandoTasa(true); }}
                style={{ marginLeft: 6, background: 'none', border: 'none', cursor: 'pointer', color: '#2BA7CE', padding: 0 }}>
                <i className="pi pi-pencil" style={{ fontSize: 12 }} />
              </button>
            )}
          </div>
          {editandoTasa ? (
            <div style={{ marginTop: 2 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input type="text" inputMode="decimal" value={tasaInput} autoFocus
                  onChange={(e) => setTasaInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') guardarTasa(); if (e.key === 'Escape') setEditandoTasa(false); }}
                  style={{ width: 64, padding: '2px 6px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 15 }} />
                <span style={{ fontSize: 14, color: '#7A8896' }}>%</span>
                <button type="button" onClick={guardarTasa} disabled={guardandoTasa}
                  style={{ background: '#00BA6E', color: '#fff', border: 'none', borderRadius: 6, padding: '3px 8px', cursor: 'pointer' }}>
                  <i className={guardandoTasa ? 'pi pi-spinner pi-spin' : 'pi pi-check'} style={{ fontSize: 12 }} />
                </button>
                <button type="button" onClick={() => setEditandoTasa(false)} disabled={guardandoTasa}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#7A8896' }}>
                  <i className="pi pi-times" style={{ fontSize: 12 }} />
                </button>
              </div>
              {tasaErr && <div style={{ color: '#DC2626', fontSize: 11.5, marginTop: 3 }}>{tasaErr}</div>}
            </div>
          ) : (
            <div className="sc-val">{r.tasa != null ? r.tasa + '%' : '—'}</div>
          )}
          <div className="sc-sub">{r.plazo_meses} meses · día {r.dia_de_pago || '—'}</div>
        </div>
        <div className="stat-card">
          <div className="sc-label">Mora acumulada</div>
          <div className="sc-val" style={{ color: Number(totales.mora_acumulada) > 0 ? '#DC2626' : undefined }}>{money(totales.mora_acumulada ?? r.mora_acumulada)}</div>
          <div className="sc-sub">{r.dias_vencido ? r.dias_vencido + ' días de atraso' : 'al corriente'}</div>
        </div>
      </div>

      {/* Pago mensual total = cuota de financiación + servicios (seguro/placas/GPS).
          El cobro lo reparte: mora → servicios del mes → cuota. */}
      {serviciosMes > 0 && cuotaBase > 0 && (
        <div className="card" style={{ padding: '10px 16px', marginBottom: 16, fontSize: 13.5, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <i className="pi pi-wallet" style={{ color: '#2BA7CE' }} />
          <b>Pago mensual total {money(cuotaBase + serviciosMes)}</b>
          <span style={{ color: '#7A8896' }}>
            = cuota {money(cuotaBase)} + servicios {money(serviciosMes)} (seguro · placas · GPS)
          </span>
        </div>
      )}

      {/* PURO: desglose informativo de la renta (la renta YA lo incluye, no se cobra
          aparte). Los componentes suman exacto a la renta contratada. Ceros visibles. */}
      {rentaDesglose && (
        <div className="card" style={{ padding: '12px 16px', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, fontSize: 13.5 }}>
            <i className="pi pi-list" style={{ color: '#2BA7CE' }} />
            <b>Desglose de la renta</b>
            <span style={{ color: '#7A8896' }}>incluido en la renta mensual · no se cobra aparte</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10 }}>
            {[
              ['Renta financiera', rentaDesglose.financiera || 0],
              ['Seguro', rentaDesglose.seguro || 0],
              ['Placas / gestorías', rentaDesglose.placas || 0],
              ['GPS / localización', rentaDesglose.gps || 0],
              // Alta manual: monto único de servicios (sin desglosar por concepto).
              ...(rentaDesglose.servicios != null ? [['Servicios (sin desglosar)', rentaDesglose.servicios]] : []),
              ['Otros servicios', rentaDesglose.otros || 0],
            ].map(([label, val]) => (
              <div key={label} style={{ background: '#F6F9FB', borderRadius: 8, padding: '8px 12px' }}>
                <div style={{ fontSize: 11.5, color: '#7A8896', textTransform: 'uppercase', letterSpacing: 0.3 }}>{label}</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: Number(val) > 0 ? '#1B2733' : '#AEB8C2' }}>
                  {money(val)}{Number(val) === 0 ? ' · sin' : ''}
                </div>
              </div>
            ))}
          </div>
          {rentaDesglose.nota && (
            <div style={{ marginTop: 8, fontSize: 12, color: '#a4640a' }}>
              <i className="pi pi-info-circle" style={{ fontSize: 11, marginRight: 4 }} />{rentaDesglose.nota}
            </div>
          )}
        </div>
      )}

      {/* Apartado de ACCESORIOS (desglose del cotizador). Se muestran los ceros para
          dejar explícito qué no trae el contrato (ej. "Seguro $0.00"). Plan: partir
          servicios_mensual en 3 campos propios; de momento el desglose vive en meta. */}
      {!esRenta && desglose && (
        <div className="card" style={{ padding: '12px 16px', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, fontSize: 13.5 }}>
            <i className="pi pi-box" style={{ color: '#2BA7CE' }} />
            <b>Accesorios / servicios</b>
            <span style={{ color: '#7A8896' }}>{money(desglose.total)} / mes</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 10 }}>
            {[
              ['Seguro', desglose.seguro],
              ['Placas / gestorías', desglose.placas],
              ['GPS / localización', desglose.gps],
              ['IVA accesorios', desglose.iva],
            ].map(([label, val]) => (
              <div key={label} style={{ background: '#F6F9FB', borderRadius: 8, padding: '8px 12px' }}>
                <div style={{ fontSize: 11.5, color: '#7A8896', textTransform: 'uppercase', letterSpacing: 0.3 }}>{label}</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: Number(val) > 0 ? '#1B2733' : '#AEB8C2' }}>
                  {money(val)}{Number(val) === 0 ? ' · sin' : ''}
                </div>
              </div>
            ))}
            {/* Total = suma de las 4 celdas (defensivo, no solo desglose.total) para
                que el cuadre sea visible aquí mismo, junto a sus componentes. */}
            <div style={{ background: '#E4F5FB', borderRadius: 8, padding: '8px 12px', border: '1px solid #BFE4F0' }}>
              <div style={{ fontSize: 11.5, color: '#1f6f8c', textTransform: 'uppercase', letterSpacing: 0.3, fontWeight: 700 }}>Total</div>
              <div style={{ fontSize: 15, fontWeight: 800, color: '#0C2036' }}>
                {money((Number(desglose.seguro) || 0) + (Number(desglose.placas) || 0) + (Number(desglose.gps) || 0) + (Number(desglose.iva) || 0))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs internos */}
      <div className="cart-tabs">
        <button className={seccion === 'cuenta' ? 'on' : ''} onClick={() => setSeccion('cuenta')}>
          Estado de cuenta {cuenta.length ? `(${cuenta.length})` : ''}
        </button>
        <button className={seccion === 'pagos' ? 'on' : ''} onClick={() => setSeccion('pagos')}>
          Pagos {pagos.length ? `(${pagos.length})` : ''}
        </button>
      </div>

      {seccion === 'cuenta' ? (
        <div className="card" style={{ overflow: 'hidden' }}>
          {cuenta.length === 0 ? (
            <div className="empty-state" style={{ padding: 36 }}>
              <i className="pi pi-list" /><div className="es-title">Sin estado de cuenta</div>
              <div className="es-sub">Este crédito no tiene parcialidades generadas.</div>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="tbl" style={{ minWidth: esRenta ? (amortPuro ? (amortSplit ? 1480 : 1200) : (desgCols.length ? 660 + desgCols.length * 110 : 620)) : (serviciosMes > 0 ? 1060 : 880) }}>
                <thead>
                  {esRenta ? (amortPuro ? (
                    <tr>
                      <th style={{ paddingLeft: 22 }}>#</th><th>Vencimiento</th>
                      <th className="right">Interés</th><th className="right">Capital</th>
                      <th className="right">Renta</th><th className="right">Renta c/IVA</th>
                      {amortSplit
                        ? <><th className="right">Seguro</th><th className="right">Placas</th><th className="right">GPS</th><th className="right">IVA acc</th></>
                        : <th className="right">Servicios</th>}
                      <th className="right">Pago mensual</th><th className="right">Saldo</th>
                      <th>Estatus</th><th style={{ paddingRight: 22 }}>Pagado</th>
                    </tr>
                  ) : (
                    <tr>
                      <th style={{ paddingLeft: 22 }}>#</th><th>Periodo</th><th>Vencimiento</th>
                      {desgCols.map(([l]) => <th key={l} className="right">{l}</th>)}
                      <th className="right">Renta</th><th>Estatus</th><th style={{ paddingRight: 22 }}>Pagado</th>
                    </tr>
                  )) : (
                    <tr>
                      <th style={{ paddingLeft: 22 }}>#</th><th>Vencimiento</th>
                      <th className="right">Capital</th><th className="right">Interés</th><th className="right">IVA</th>
                      <th className="right">Cuota</th><th className="right">Saldo</th>
                      {serviciosMes > 0 && <><th className="right">Servicios</th><th className="right">Pago mensual</th></>}
                      <th style={{ paddingRight: 22 }}>Estatus</th>
                    </tr>
                  )}
                </thead>
                <tbody>
                  {cuenta.map((p) => esRenta ? (amortPuro ? (
                    <tr key={p.numero}>
                      <td style={{ paddingLeft: 22 }} className="fw6">{p.numero}</td>
                      <td className="t-sm muted">{p.fecha_vencimiento || '—'}</td>
                      <td className="right t-sm">{p.amort ? money(p.amort.interes) : '—'}</td>
                      <td className="right t-sm">{p.amort ? money(p.amort.capital) : '—'}</td>
                      <td className="right t-sm muted">{p.amort ? money(p.amort.renta_base) : '—'}</td>
                      <td className="right t-sm">{p.amort ? money(p.amort.renta_base_civa) : '—'}</td>
                      {amortSplit ? (
                        <>
                          <td className="right t-sm muted">{p.amort ? money(p.amort.seguro) : '—'}</td>
                          <td className="right t-sm muted">{p.amort ? money(p.amort.placas) : '—'}</td>
                          <td className="right t-sm muted">{p.amort ? money(p.amort.gps) : '—'}</td>
                          <td className="right t-sm muted">{p.amort ? money(p.amort.iva_acc) : '—'}</td>
                        </>
                      ) : (
                        <td className="right t-sm muted">{p.amort ? money(p.amort.servicios) : '—'}</td>
                      )}
                      <td className="right"><span className="money">{money(p.pago_total)}</span></td>
                      <td className="right t-sm muted">{p.amort ? money(p.amort.saldo_final) : '—'}</td>
                      <td><span className={'pago-badge ' + p.estatus}>{estatusLabel(p)}</span></td>
                      <td style={{ paddingRight: 22 }} className="t-xs muted">
                        {p.estatus === 'partial'
                          ? <>abonó {money(p.monto_pagado)} · <span style={{ color: '#b91c1c', fontWeight: 600 }}>falta {money(p.restante)}</span></>
                          : (p.pagado_en ? String(p.pagado_en).slice(0, 10) : '—')}
                      </td>
                    </tr>
                  ) : (
                    <tr key={p.numero}>
                      <td style={{ paddingLeft: 22 }} className="fw6">{p.numero}</td>
                      <td className="t-sm">{p.periodo || `Mes ${p.numero}`}</td>
                      <td className="t-sm muted">{p.fecha_vencimiento || '—'}</td>
                      {/* Desglose constante por mes; si la renta del mes difiere (ej. anticipo), '—'. */}
                      {desgCols.map(([l, v]) => (
                        <td key={l} className="right t-sm muted">
                          {Math.abs(Number(p.pago_total || 0) - desgSuma) <= 1 ? money(v) : '—'}
                        </td>
                      ))}
                      <td className="right"><span className="money">{money(p.pago_total)}</span></td>
                      <td><span className={'pago-badge ' + p.estatus}>{estatusLabel(p)}</span></td>
                      <td style={{ paddingRight: 22 }} className="t-xs muted">
                        {p.estatus === 'partial'
                          ? <>abonó {money(p.monto_pagado)} · <span style={{ color: '#b91c1c', fontWeight: 600 }}>falta {money(p.restante)}</span></>
                          : (p.pagado_en ? String(p.pagado_en).slice(0, 10) : '—')}
                      </td>
                    </tr>
                  )) : (
                    <tr key={p.numero}>
                      <td style={{ paddingLeft: 22 }} className="fw6">{p.numero}</td>
                      <td className="t-sm muted">{p.fecha_vencimiento || '—'}</td>
                      <td className="right t-sm">{money(p.capital)}</td>
                      <td className="right t-sm">{money(p.interes)}</td>
                      <td className="right t-sm muted">{money(p.iva_interes)}</td>
                      <td className="right"><span className="money">{money(p.pago_total)}</span></td>
                      <td className="right t-sm muted">{money(p.saldo_restante)}</td>
                      {serviciosMes > 0 && (
                        <>
                          <td className="right t-sm muted">{money(serviciosMes)}</td>
                          <td className="right"><span className="money">{money(Number(p.pago_total || 0) + serviciosMes)}</span></td>
                        </>
                      )}
                      <td style={{ paddingRight: 22 }}>
                        <span className={'pago-badge ' + p.estatus}>{estatusLabel(p)}</span>
                        {p.estatus === 'pago_parcial' && (
                          <div className="t-xs" style={{ color: '#b91c1c', fontWeight: 600, marginTop: 2 }}>
                            falta {money(debeDe(p))}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                  {/* Recuperación de auto que sigue cobrando un saldo: fila
                      informativa al final (no es una cuota). colSpan = columnas de
                      la variante que corresponda: AP puro (10 + servicios 1|4), AP
                      (6 + desglose) o AF/CS (8, +2 con servicios). */}
                  {cobrosRec.length > 0 && (
                    <tr>
                      <td colSpan={numCols} style={{ padding: '10px 22px', background: '#F5F3FF', color: '#5B21B6', fontSize: 13, fontWeight: 600 }}>
                        <i className="pi pi-car" style={{ marginRight: 8 }} />
                        {cobrosRec.map((cb, i) => (
                          <div key={cb.persona} style={i > 0 ? { marginTop: 4 } : undefined}>
                            {(cobrosRec.length > 1 || cb.persona === 'aval') && <>{cb.quien}: </>}
                            Saldo pendiente tras recuperación {money(cb.monto)} · {cb.pagado ? `Pagado el ${fechaCorta(cb.fecha_pago)}` : 'No pagado'}
                          </div>
                        ))}
                      </td>
                    </tr>
                  )}
                </tbody>
                {/* Pie con DOS filas: "Σ Por cobrar" (solo no pagadas, DESCONTANDO
                    lo abonado en parciales — así Capital empata con el saldo) y
                    "Σ Total" (contrato completo). Solo columnas de flujo; Saldo no
                    se suma ('—'). Las columnas condicionales repiten el mismo
                    condicional del thead/tbody. */}
                <tfoot>
                  {esRenta ? (amortPuro ? (
                    <>
                      <tr className="tf-pend">
                        <td style={{ paddingLeft: 22 }} colSpan={2}>Σ Por cobrar</td>
                        <td className="right t-sm">{money(sumPend((p) => p.amort?.interes))}</td>
                        <td className="right t-sm">{money(sumPend((p) => p.amort?.capital))}</td>
                        <td className="right t-sm">{money(sumPend((p) => p.amort?.renta_base))}</td>
                        <td className="right t-sm">{money(sumPend((p) => p.amort?.renta_base_civa))}</td>
                        {amortSplit ? (
                          <>
                            <td className="right t-sm">{money(sumPend((p) => p.amort?.seguro))}</td>
                            <td className="right t-sm">{money(sumPend((p) => p.amort?.placas))}</td>
                            <td className="right t-sm">{money(sumPend((p) => p.amort?.gps))}</td>
                            <td className="right t-sm">{money(sumPend((p) => p.amort?.iva_acc))}</td>
                          </>
                        ) : (
                          <td className="right t-sm">{money(sumPend((p) => p.amort?.servicios))}</td>
                        )}
                        {/* La renta por cobrar descuenta lo abonado en parciales
                            (composición referencial se muestra completa). */}
                        <td className="right"><span className="money">{money(sumPendResta())}</span></td>
                        <td className="right t-sm muted">—</td>
                        <td />
                        <td style={{ paddingRight: 22 }} className="t-sm">{money(sumPend((p) => p.monto_pagado))}</td>
                      </tr>
                      <tr>
                        <td style={{ paddingLeft: 22 }} colSpan={2}>Σ Total</td>
                        <td className="right t-sm">{money(sum((p) => p.amort?.interes))}</td>
                        <td className="right t-sm">{money(sum((p) => p.amort?.capital))}</td>
                        <td className="right t-sm">{money(sum((p) => p.amort?.renta_base))}</td>
                        <td className="right t-sm">{money(sum((p) => p.amort?.renta_base_civa))}</td>
                        {amortSplit ? (
                          <>
                            <td className="right t-sm">{money(sum((p) => p.amort?.seguro))}</td>
                            <td className="right t-sm">{money(sum((p) => p.amort?.placas))}</td>
                            <td className="right t-sm">{money(sum((p) => p.amort?.gps))}</td>
                            <td className="right t-sm">{money(sum((p) => p.amort?.iva_acc))}</td>
                          </>
                        ) : (
                          <td className="right t-sm">{money(sum((p) => p.amort?.servicios))}</td>
                        )}
                        <td className="right"><span className="money">{money(sum((p) => p.pago_total))}</span></td>
                        <td className="right t-sm muted">—</td>
                        <td />
                        <td style={{ paddingRight: 22 }} className="t-sm">{money(sum((p) => p.monto_pagado))}</td>
                      </tr>
                    </>
                  ) : (
                    <>
                      <tr className="tf-pend">
                        <td style={{ paddingLeft: 22 }} colSpan={3}>Σ Por cobrar</td>
                        {desgCols.map(([l, v]) => (
                          <td key={l} className="right t-sm">{money(v * desgFilasPend)}</td>
                        ))}
                        <td className="right"><span className="money">{money(sumPendResta())}</span></td>
                        <td />
                        <td style={{ paddingRight: 22 }} className="t-sm">{money(sumPend((p) => p.monto_pagado))}</td>
                      </tr>
                      <tr>
                        <td style={{ paddingLeft: 22 }} colSpan={3}>Σ Total</td>
                        {desgCols.map(([l, v]) => (
                          <td key={l} className="right t-sm">{money(v * desgFilas)}</td>
                        ))}
                        <td className="right"><span className="money">{money(sum((p) => p.pago_total))}</span></td>
                        <td />
                        <td style={{ paddingRight: 22 }} className="t-sm">{money(sum((p) => p.monto_pagado))}</td>
                      </tr>
                    </>
                  )) : (
                    <>
                      <tr className="tf-pend">
                        <td style={{ paddingLeft: 22 }} colSpan={2}>Σ Por cobrar</td>
                        <td className="right t-sm">{money(sumPendRest('capital'))}</td>
                        <td className="right t-sm">{money(sumPendRest('interes'))}</td>
                        <td className="right t-sm">{money(sumPendRest('iva'))}</td>
                        <td className="right"><span className="money">{money(sumPendRest('cuota'))}</span></td>
                        <td className="right t-sm muted">—</td>
                        {serviciosMes > 0 && (
                          <>
                            <td className="right t-sm">{money(sumPendRest('servicios'))}</td>
                            <td className="right"><span className="money">{money(sumPendRest('cuota') + sumPendRest('servicios'))}</span></td>
                          </>
                        )}
                        <td style={{ paddingRight: 22 }} />
                      </tr>
                      <tr>
                        <td style={{ paddingLeft: 22 }} colSpan={2}>Σ Total</td>
                        <td className="right t-sm">{money(sum((p) => p.capital))}</td>
                        <td className="right t-sm">{money(sum((p) => p.interes))}</td>
                        <td className="right t-sm">{money(sum((p) => p.iva_interes))}</td>
                        <td className="right"><span className="money">{money(sum((p) => p.pago_total))}</span></td>
                        <td className="right t-sm muted">—</td>
                        {serviciosMes > 0 && (
                          <>
                            <td className="right t-sm">{money(serviciosMes * cuenta.length)}</td>
                            <td className="right"><span className="money">{money(sum((p) => Number(p.pago_total || 0) + serviciosMes))}</span></td>
                          </>
                        )}
                        <td style={{ paddingRight: 22 }} />
                      </tr>
                    </>
                  )}
                </tfoot>
              </table>
            </div>
          )}
          {/* Nota discreta: la tabla pendiente se regeneró por abono(s) a capital
              (misma cuota, menos plazo). Las filas ya pagadas no se tocaron. */}
          {abonosCapital.length > 0 && (
            <div style={{ padding: '10px 22px', borderTop: '1px solid #EEF2F6' }}>
              {abonosCapital.map((a, i) => (
                <div key={i} className="t-xs muted">
                  <i className="pi pi-refresh" style={{ fontSize: 10, marginRight: 5 }} />
                  Tabla recalculada por abono a capital de {money(a.monto)} el {String(a.fecha || '').slice(0, 10) || '—'} (ahorro {money(a.ahorro ?? a.interes_ahorrado)})
                </div>
              ))}
            </div>
          )}
          {/* Identidad contable: valida que la tabla cuadre con el resumen (±$1).
              Si falta el dato base la nota no se muestra (defensivo). */}
          {notasIdentidad.length > 0 && (
            <div style={{ padding: '10px 22px', borderTop: '1px solid #EEF2F6' }}>
              {notasIdentidad.map((n, i) => (
                <div key={i} className="t-xs" style={{ color: n.ok ? '#06794b' : '#a4640a', fontWeight: 600 }}>
                  {n.ok ? '✓' : '⚠️'} {n.texto}
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="card" style={{ overflow: 'hidden' }}>
          {pagos.length === 0 ? (
            <div className="empty-state" style={{ padding: 36 }}>
              <i className="pi pi-inbox" /><div className="es-title">Sin pagos registrados</div>
              <div className="es-sub">Los pagos aplicados a este crédito aparecerán aquí.</div>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="tbl" style={{ minWidth: serviciosMes > 0 ? 1060 : 980 }}>
                <thead>
                  <tr>
                    <th style={{ paddingLeft: 22 }}>Folio</th><th>Fecha</th><th>Canal</th><th>Capturó</th>
                    <th className="right">Monto</th>
                    {serviciosMes > 0 && <th className="right">Servicios</th>}
                    <th className="right">Capital</th><th className="right">Interés</th>
                    <th className="right">Mora</th><th>Estatus</th>
                    <th style={{ paddingRight: 22 }}>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {pagos.map((p) => {
                    // El permiso lo dicta el BACK (puede_revertir + bloqueo): LIFO,
                    // mes cerrado, pago importado sin snapshot, abono a capital…
                    // Aquí no se reimplementa ninguna regla, solo se pinta.
                    const aplicado = p.estatus === 'aplicado';
                    const revertido = p.estatus === 'cancelado';
                    const puede = p.puede_revertir === true;
                    const bloqueo = p.bloqueo || 'Este pago no se puede modificar.';
                    const condonado = Number(p.mora_condonada) > 0;
                    return (
                      <tr key={p.id}>
                        <td style={{ paddingLeft: 22 }} className="fw6">{p.folio}</td>
                        <td className="t-sm muted">{p.fecha_pago || '—'}</td>
                        <td className="t-sm">{p.canal_pago || '—'}</td>
                        {/* Importados/planchados no traen quién los capturó. */}
                        <td className="t-sm">{p.capturado_por || <span className="muted">—</span>}</td>
                        <td className="right"><span className="money">{money(p.monto_pagado)}</span></td>
                        {serviciosMes > 0 && <td className="right t-sm">{money(p.aplicacion?.cargos)}</td>}
                        <td className="right t-sm">{money(p.aplicacion?.capital)}</td>
                        <td className="right t-sm">{money(p.aplicacion?.interes)}</td>
                        <td className="right t-sm">
                          {money(p.aplicacion?.mora)}
                          {condonado && (
                            <div className="t-xs" style={{ color: '#a4640a', fontWeight: 600 }}>condonada {money(p.mora_condonada)}</div>
                          )}
                        </td>
                        <td>
                          <span className={'pago-badge ' + (PAGO_BADGE[p.estatus] || 'pendiente')}>
                            {PAGO_ESTATUS[p.estatus] || p.estatus}
                          </span>
                          {/* Toda la trazabilidad del recibo vive bajo su estatus: reversión,
                              correcciones y la nota capturada. Va aquí y no bajo el folio
                              porque son estados/historia del pago, no identidad del recibo. */}
                          {revertido && p.reversion && (
                            <div className="t-xs pg-rev-note">
                              <i className="pi pi-undo" />
                              por {p.reversion.nombre || '—'} · {String(p.reversion.fecha || '').replace('T', ' ').slice(0, 16) || '—'}
                              {p.reversion.motivo ? ` · ${p.reversion.motivo}` : ''}
                            </div>
                          )}
                          {Number(p.correcciones) > 0 && (
                            <div
                              className="t-xs pg-trace"
                              title={[
                                `Corregido ${p.correcciones} ${Number(p.correcciones) === 1 ? 'vez' : 'veces'}`,
                                p.correccion_ultima?.nombre ? `por ${p.correccion_ultima.nombre}` : null,
                                String(p.correccion_ultima?.fecha || '').replace('T', ' ').slice(0, 16) || null,
                              ].filter(Boolean).join(' · ')}
                            >
                              <i className="pi pi-pencil" />
                              Corregido{Number(p.correcciones) > 1 ? ` ${p.correcciones}×` : ''}
                              {p.correccion_ultima?.motivo ? `: ${p.correccion_ultima.motivo}` : ''}
                            </div>
                          )}
                          {p.notas && (
                            <div className="t-xs pg-trace" title={p.notas}>
                              <i className="pi pi-comment" />{p.notas}
                            </div>
                          )}
                        </td>
                        <td style={{ paddingRight: 22 }}>
                          {/* Botones directos (no dropdown): el contenedor de la
                              tabla tiene overflow-x y recortaría un menú flotante.
                              El tooltip (title) muestra el bloqueo tal cual. */}
                          {!aplicado ? <span className="t-xs muted">—</span> : (
                            <div className="pg-acts">
                              <button type="button" className="pg-act" disabled={!puede}
                                title={puede ? 'Deshacer este pago' : bloqueo}
                                onClick={() => setAccionPago({ pago: p, modo: 'revertir' })}>
                                <i className="pi pi-undo" />
                              </button>
                              <button type="button" className="pg-act" disabled={!puede}
                                title={puede ? 'Corregir monto, fecha, canal o notas' : bloqueo}
                                onClick={() => setAccionPago({ pago: p, modo: 'corregir' })}>
                                <i className="pi pi-pencil" />
                              </button>
                              {/* Condonar no exige LIFO (es contable, no toca cuotas):
                                  se ofrece según lo que muestra el pago — ya condonó
                                  (se puede quitar) o el crédito trae mora viva. */}
                              {condonado ? (
                                <button type="button" className="pg-act pg-act-amber"
                                  title={`Quitar la condonación de ${money(p.mora_condonada)}: la mora vuelve a correr`}
                                  onClick={() => setAccionPago({ pago: p, modo: 'condonar', accion: 'quitar' })}>
                                  <i className="pi pi-ban" />
                                </button>
                              ) : moraActual > 0 ? (
                                <button type="button" className="pg-act pg-act-amber"
                                  title={`Condonar la mora acumulada de ${money(moraActual)}`}
                                  onClick={() => setAccionPago({ pago: p, modo: 'condonar', accion: 'aplicar' })}>
                                  <i className="pi pi-percentage" />
                                </button>
                              ) : null}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      {showPago && (
        <RegistrarPagoModal
          credito={r}
          proximaMensualidad={proximaMensualidad}
          alCorriente={alCorriente}
          completarCuota={completarCuota}
          maxPagar={maxPagar}
          onClose={() => setShowPago(false)}
          onDone={load}
        />
      )}
      {showRecuperar && (
        <RecuperarAutoModal credito={r} onClose={() => setShowRecuperar(false)} onDone={load} />
      )}
      {showCambiarBuro && recuperado && (
        <CambiarResultadoBuroModal credito={r} cierre={cierre} onClose={() => setShowCambiarBuro(false)} onDone={load} />
      )}
      {showDeshacerRec && (
        <RevertirRecuperacionModal credito={r} onClose={() => setShowDeshacerRec(false)} onDone={load} />
      )}
      {/* Deshacer / corregir / (des)condonar un pago ya registrado. Al terminar
          se recarga el detalle: saldo, cuotas, mora y totales cambiaron. */}
      {accionPago && (
        <RevertirPagoModal
          credito={r}
          pago={accionPago.pago}
          modoInicial={accionPago.modo}
          accionCondonacion={accionPago.accion || 'quitar'}
          mora={moraActual}
          onClose={() => setAccionPago(null)}
          onDone={load}
        />
      )}
    </>
  );
};

export default CarteraDetalle;
